/**
 * Program monitor. Every scrub and every export frame goes through the same
 * compositor, so the stage is the movie, not a design-canvas poster.
 */

import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import type { ProjectPage, VideoProject } from '@canvas/shared-types';
import { createFrameCompositor, type FrameCompositor } from '@/lib/video/frameCompositor';
import { topClipAt, type TextMeasure } from '@/lib/video/clipFrame';
import { REEL_CAPTION_KEY } from '@/lib/reel/reelCaption';
import { isClipProject } from '@/lib/video/timelineEdit';
import { useVideoStore } from '@/stores/videoStore';
import ClipOverlay from './ClipOverlay';

/** Stable across new page arrays. Changes when a scene picture or its caption changes. */
function pageSignature(pages: ProjectPage[]): string {
  return pages.map((page) => {
    const objects = page.canvasData?.objects || [];
    let caption = '';
    for (const obj of objects) {
      const record = obj as Record<string, unknown>;
      if (record[REEL_CAPTION_KEY] === true && typeof record.text === 'string') caption = record.text;
    }
    return `${page.pageId}:${objects.length}:${caption}:${String(page.canvasData?.background ?? '')}`;
  }).join('|');
}

interface ProgramMonitorProps {
  pages: ProjectPage[];
}

export default function ProgramMonitor({ pages }: ProgramMonitorProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const compositorRef = useRef<FrameCompositor | null>(null);
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const video = useVideoStore((s) => s.video);
  const time = useVideoStore((s) => s.currentTimeMs);
  const width = video?.canvas?.width || video?.exportSettings.width || 1920;
  const height = video?.canvas?.height || video?.exportSettings.height || 1080;
  const pagesSig = pageSignature(pages);
  const follow = useRef({ videoStamp: video?.updatedAt ?? 0, pagesSig });
  const longEdge = 720;
  const fitted = Math.min(1, longEdge / Math.max(width, height));
  const frame = {
    width: Math.max(2, Math.round(width * fitted)),
    height: Math.max(2, Math.round(height * fitted)),
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const current = useVideoStore.getState().video;
    if (!canvas || !current) return;
    canvas.width = frame.width;
    canvas.height = frame.height;
    let dead = false;
    let compositor: FrameCompositor | null = null;
    void createFrameCompositor({ pages: pagesRef.current, video: current, target: canvas })
      .then((created) => {
        if (dead) {
          created.dispose();
          return;
        }
        compositor = created;
        compositorRef.current = created;
        const latest = useVideoStore.getState().video;
        if (latest) created.replaceProject?.(latest);
        created.setPages?.(pagesRef.current);
        follow.current = {
          videoStamp: latest?.updatedAt ?? 0,
          pagesSig: pageSignature(pagesRef.current),
        };
        return created.renderFrameAt(useVideoStore.getState().currentTimeMs);
      })
      .catch(() => undefined);
    return () => {
      dead = true;
      compositor?.dispose();
      if (compositorRef.current === compositor) compositorRef.current = null;
    };
  }, [frame.width, frame.height]);

  useEffect(() => {
    const compositor = compositorRef.current;
    const current = useVideoStore.getState().video;
    if (!compositor?.replaceProject || !current) return;
    const videoStamp = current.updatedAt ?? 0;
    if (follow.current.videoStamp !== videoStamp || follow.current.pagesSig !== pagesSig) {
      follow.current = { videoStamp, pagesSig };
      compositor.setPages?.(pagesRef.current);
      compositor.replaceProject(current);
    }
    void compositor.renderFrameAt(time).catch(() => undefined);
  }, [time, video?.updatedAt, pagesSig]);

  const onLive = (next: VideoProject): void => {
    const compositor = compositorRef.current;
    if (!compositor?.replaceProject) return;
    compositor.replaceProject(next);
    void compositor.renderFrameAt(useVideoStore.getState().currentTimeMs).catch(() => undefined);
  };

  const onPick = (event: ReactPointerEvent<HTMLCanvasElement>): void => {
    if (!isClipProject(video)) return;
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / Math.max(1, rect.width)) * canvas.width;
    const py = ((event.clientY - rect.top) / Math.max(1, rect.height)) * canvas.height;
    const measure: TextMeasure = (text, font) => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return text.length * 10;
      ctx.font = font;
      return ctx.measureText(text || ' ').width;
    };
    const hit = topClipAt(video, time, { width: canvas.width, height: canvas.height }, px, py, measure);
    useVideoStore.getState().selectClip(hit?.id ?? null);
  };

  return (
    <div className="vt-monitor-wrap">
      <div
        className="vt-monitor-stage"
        style={{
          aspectRatio: `${width} / ${height}`,
          width: `min(100cqw, 720px, calc(100cqh * ${width} / ${height}))`,
        }}
      >
        <canvas
          ref={canvasRef}
          className="vt-monitor"
          onPointerDown={onPick}
        />
        <ClipOverlay frame={frame} canvasRef={canvasRef} onLive={onLive} />
      </div>
    </div>
  );
}
